import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Stack,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  IconButton,
  Tooltip,
  Skeleton,
  Alert,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  TextField,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import LockResetIcon from "@mui/icons-material/LockReset";
import PersonAddIcon from "@mui/icons-material/PersonAddAlt1";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import RemoveModeratorIcon from "@mui/icons-material/RemoveModeratorOutlined";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { get, post } from "aws-amplify/api";
import Moment from "moment";
import { useAuth } from "../libs/AuthContext";

const MIN_ADMINS = 2;

// Per-column sort key extractor. Versions ("1.4.2") are turned into a
// zero-padded string so a plain string compare orders 1.10.0 > 1.9.0.
// ISO date strings already sort lexicographically.
function sortValue(row, column) {
  switch (column) {
    case "email":           return (row.email ?? "").toLowerCase();
    case "role":            return row.isAdmin ? 1 : 0;
    case "createdDateTime": return row.createdDateTime ?? "";
    case "lastAccessed":    return row.lastAccessed ?? "";
    case "lastVersion":
      if (!row.lastVersion) return "";
      return row.lastVersion
        .split(".")
        .map((p) => p.padStart(8, "0"))
        .join(".");
    default: return "";
  }
}

/**
 * Accepts any of the response shapes we've seen for /list-users:
 *   1. New clean: { users: [{ userId, email, createdDateTime, lastVersion, lastAccessed, isAdmin }] }
 *   2. Legacy raw DynamoDB scan: { Items: [{ UserId: {S}, Email: {S}, ... }] }
 *   3. Legacy PascalCase: { users: [{ UserId, UserEmail, UserCreated, LastAccessed, LastVersion }] }
 *   4. Bare array of any of the above row shapes
 */
function normalizeUsers(response) {
  if (!response) return [];

  let items = [];
  if (Array.isArray(response)) items = response;
  else if (Array.isArray(response.users)) items = response.users;
  else if (Array.isArray(response.Items)) items = response.Items;
  else return [];

  // Unwrap DynamoDB type-tagged values like { "S": "value" }, { "N": "5" }, otherwise pass through.
  const unwrap = (v) => {
    if (v && typeof v === "object" && !Array.isArray(v)) {
      if ("S" in v) return v.S;
      if ("N" in v) return v.N;
      if ("BOOL" in v) return v.BOOL;
    }
    return v;
  };
  const pick = (obj, ...keys) => {
    for (const k of keys) {
      if (obj?.[k] != null) return unwrap(obj[k]);
    }
    return undefined;
  };

  return items.map((item) => ({
    userId: pick(item, "userId", "UserId") ?? "",
    email: pick(item, "email", "Email", "UserEmail") ?? "",
    createdDateTime: pick(item, "createdDateTime", "CreatedDateTime", "UserCreated") ?? "",
    lastVersion: pick(item, "lastVersion", "LastVersion") ?? "",
    lastAccessed:
      pick(item, "lastAccessed", "LastAccessed", "LastRetrievedLicenseKey") ?? null,
    isAdmin: Boolean(pick(item, "isAdmin", "IsAdmin")) || false
  }));
}

export default function UserAdminList() {
  const { isAuthenticated, emailAddress } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [error, setError] = useState("");
  const [confirmAction, setConfirmAction] = useState(null);
  const [actionInProgress, setActionInProgress] = useState(false);
  const [snackbar, setSnackbar] = useState(null);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [sortBy, setSortBy] = useState("email");
  const [sortDir, setSortDir] = useState("asc");

  useEffect(() => {
    if (!isAuthenticated) return;
    (async () => {
      try {
        const op = get({ apiName: "license", path: "/list-users" });
        const { body } = await op.response;
        const result = await body.json();
        setUsers(normalizeUsers(result));
      } catch (e) {
        setError(e?.message ?? "Failed to load users");
      } finally {
        setIsLoading(false);
      }
    })();
  }, [isAuthenticated]);

  const adminCount = useMemo(() => users.filter((u) => u.isAdmin).length, [users]);

  const filteredUsers = useMemo(() => {
    const term = search.trim().toLowerCase();
    const rows = users.filter((u) => {
      if (roleFilter === "admin" && !u.isAdmin) return false;
      if (roleFilter === "non-admin" && u.isAdmin) return false;
      if (term && !(u.email ?? "").toLowerCase().includes(term)) return false;
      return true;
    });
    const dir = sortDir === "asc" ? 1 : -1;
    // Nulls/empties always sort to the bottom regardless of direction —
    // a user who has never signed in shouldn't bubble to the top of an
    // "oldest first" view just because their lastAccessed is empty.
    const cmp = (a, b) => {
      const va = sortValue(a, sortBy);
      const vb = sortValue(b, sortBy);
      const aMissing = va == null || va === "";
      const bMissing = vb == null || vb === "";
      if (aMissing && bMissing) return 0;
      if (aMissing) return 1;
      if (bMissing) return -1;
      if (va < vb) return -1 * dir;
      if (va > vb) return  1 * dir;
      return 0;
    };
    return [...rows].sort(cmp);
  }, [users, search, roleFilter, sortBy, sortDir]);

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(column);
      setSortDir("asc");
    }
  };

  const isFiltering = search.trim() !== "" || roleFilter !== "all";

  const requestToggleAdmin = (row) => {
    setConfirmAction({ row, makeAdmin: !row.isAdmin });
  };

  const cancelConfirm = () => {
    if (!actionInProgress) setConfirmAction(null);
  };

  const performToggle = async () => {
    if (!confirmAction) return;
    const { row, makeAdmin } = confirmAction;
    setActionInProgress(true);
    try {
      const op = post({
        apiName: "license",
        path: "/set-admin",
        options: { body: { userid: row.userId, admin: makeAdmin } }
      });
      await op.response;
      setUsers((prev) =>
        prev.map((u) => (u.userId === row.userId ? { ...u, isAdmin: makeAdmin } : u))
      );
      setSnackbar({
        severity: "success",
        message: makeAdmin
          ? `${row.email} is now an admin`
          : `${row.email} is no longer an admin`
      });
      setConfirmAction(null);
    } catch (e) {
      let message = e?.message ?? "Failed to update admin status";
      try {
        const errorBody = await e?.response?.body?.json?.();
        if (errorBody?.error === "AT_LEAST_TWO_ADMINS_REQUIRED") {
          message = `At least ${MIN_ADMINS} admins are required.`;
        } else if (errorBody?.error === "SELF_DEMOTE_NOT_ALLOWED") {
          message = "You can't change your own admin access.";
        } else if (errorBody?.detail) {
          message = errorBody.detail;
        }
      } catch (_) {
        // body parse failed; fall through with default message
      }
      setSnackbar({ severity: "error", message });
    } finally {
      setActionInProgress(false);
    }
  };

  const renderAdminToggle = (row) => {
    const isSelf = row.email && emailAddress && row.email.toLowerCase() === emailAddress.toLowerCase();
    const wouldDropBelowMin = row.isAdmin && adminCount <= MIN_ADMINS;
    const disabledReason = isSelf
      ? "You can't change your own admin access"
      : wouldDropBelowMin
      ? `At least ${MIN_ADMINS} admins are required`
      : null;
    const tooltipTitle = disabledReason ?? (row.isAdmin ? "Remove admin" : "Make admin");

    return (
      <Tooltip title={tooltipTitle}>
        <span>
          <IconButton
            size="small"
            color={row.isAdmin ? "secondary" : "default"}
            disabled={Boolean(disabledReason) || actionInProgress}
            onClick={() => requestToggleAdmin(row)}
          >
            {row.isAdmin ? (
              <RemoveModeratorIcon fontSize="small" />
            ) : (
              <AdminPanelSettingsIcon fontSize="small" />
            )}
          </IconButton>
        </span>
      </Tooltip>
    );
  };

  return (
    <Box>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 3, alignItems: { sm: "center" } }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: "-0.02em" }}>
            Licensed users
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isFiltering
              ? `${filteredUsers.length} of ${users.length} ${users.length === 1 ? "user" : "users"}`
              : `${users.length} ${users.length === 1 ? "user" : "users"}`}
            {adminCount > 0 && ` · ${adminCount} ${adminCount === 1 ? "admin" : "admins"}`}
          </Typography>
        </Box>
        <Button
          startIcon={<PersonAddIcon />}
          variant="contained"
          onClick={() => navigate("/admin/register")}
        >
          Register user
        </Button>
      </Stack>

      {error ? <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert> : null}

      {!isLoading && users.length > 0 && (
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          sx={{ mb: 2, alignItems: { sm: "center" } }}
        >
          <TextField
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by email"
            size="small"
            sx={{ flex: 1, maxWidth: { sm: 360 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: "text.secondary" }} />
                </InputAdornment>
              ),
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearch("")} aria-label="Clear search">
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null
            }}
          />
          <ToggleButtonGroup
            value={roleFilter}
            exclusive
            onChange={(_, v) => v && setRoleFilter(v)}
            size="small"
            sx={{ "& .MuiToggleButton-root": { px: 2, textTransform: "none", fontWeight: 500 } }}
          >
            <ToggleButton value="all">All</ToggleButton>
            <ToggleButton value="admin">Admins</ToggleButton>
            <ToggleButton value="non-admin">Non-admins</ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      )}

      <Card sx={{ overflow: "hidden" }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sortDirection={sortBy === "email" ? sortDir : false}>
                  <TableSortLabel
                    active={sortBy === "email"}
                    direction={sortBy === "email" ? sortDir : "asc"}
                    onClick={() => handleSort("email")}
                  >
                    Email
                  </TableSortLabel>
                </TableCell>
                <TableCell width={100} sortDirection={sortBy === "role" ? sortDir : false}>
                  <TableSortLabel
                    active={sortBy === "role"}
                    direction={sortBy === "role" ? sortDir : "asc"}
                    onClick={() => handleSort("role")}
                  >
                    Role
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortBy === "createdDateTime" ? sortDir : false}>
                  <TableSortLabel
                    active={sortBy === "createdDateTime"}
                    direction={sortBy === "createdDateTime" ? sortDir : "asc"}
                    onClick={() => handleSort("createdDateTime")}
                  >
                    Created
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortBy === "lastAccessed" ? sortDir : false}>
                  <TableSortLabel
                    active={sortBy === "lastAccessed"}
                    direction={sortBy === "lastAccessed" ? sortDir : "asc"}
                    onClick={() => handleSort("lastAccessed")}
                  >
                    Last accessed
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortBy === "lastVersion" ? sortDir : false}>
                  <TableSortLabel
                    active={sortBy === "lastVersion"}
                    direction={sortBy === "lastVersion" ? sortDir : "asc"}
                    onClick={() => handleSort("lastVersion")}
                  >
                    Last version
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" width={150}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={6}><Skeleton variant="text" /></TableCell>
                  </TableRow>
                ))
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} sx={{ textAlign: "center", py: 6, color: "text.secondary" }}>
                    No registered users yet.
                  </TableCell>
                </TableRow>
              ) : filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} sx={{ textAlign: "center", py: 6, color: "text.secondary" }}>
                    <Stack spacing={1.5} alignItems="center">
                      <Typography variant="body2">
                        No users match {search ? <>"<b>{search}</b>"</> : "the current filter"}.
                      </Typography>
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => { setSearch(""); setRoleFilter("all"); }}
                      >
                        Clear filters
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ) : (
                filteredUsers.map((row) => (
                  <TableRow key={row.userId} hover>
                    <TableCell sx={{ fontWeight: 500 }}>{row.email}</TableCell>
                    <TableCell>
                      {row.isAdmin ? (
                        <Chip label="Admin" size="small" color="secondary" sx={{ fontWeight: 500 }} />
                      ) : (
                        <Typography variant="body2" color="text.disabled">—</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ color: "text.secondary" }}>
                      {Moment(row.createdDateTime).format("MMM D, YYYY")}
                    </TableCell>
                    <TableCell sx={{ color: "text.secondary" }}>
                      {row.lastAccessed ? Moment(row.lastAccessed).format("MMM D, YYYY") : "Never"}
                    </TableCell>
                    <TableCell>
                      {row.lastVersion ? <Chip label={row.lastVersion} size="small" variant="outlined" /> : "—"}
                    </TableCell>
                    <TableCell align="right">
                      {renderAdminToggle(row)}
                      <Tooltip title="Reset password">
                        <IconButton
                          size="small"
                          onClick={() =>
                            navigate("/admin/password/reset", {
                              state: { userId: row.userId, email: row.email }
                            })
                          }
                        >
                          <LockResetIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Unregister user">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() =>
                            navigate("/admin/unregister", {
                              state: { userId: row.userId, email: row.email }
                            })
                          }
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      <Dialog open={Boolean(confirmAction)} onClose={cancelConfirm} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>
          {confirmAction?.makeAdmin ? "Make admin?" : "Remove admin access?"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: "text.primary" }}>
            {confirmAction?.makeAdmin ? (
              <>
                <strong>{confirmAction?.row.email}</strong> will be able to manage users, register new users, reset passwords, and grant admin access to others.
              </>
            ) : (
              <>
                <strong>{confirmAction?.row.email}</strong> will lose access to the admin section. They'll keep their licence and can sign in as a regular user.
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={cancelConfirm} disabled={actionInProgress}>Cancel</Button>
          <Button
            onClick={performToggle}
            variant="contained"
            color={confirmAction?.makeAdmin ? "primary" : "error"}
            disabled={actionInProgress}
          >
            {actionInProgress
              ? "Working…"
              : confirmAction?.makeAdmin
              ? "Make admin"
              : "Remove admin"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(snackbar)}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {snackbar ? (
          <Alert severity={snackbar.severity} variant="filled" onClose={() => setSnackbar(null)}>
            {snackbar.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
}
